// ============================================================================
// Rende leggibile la risposta dell'AI Copywriter.
//
// L'assistente risponde in testo semplice con le etichette GANCIO / CORPO / CTA
// (vedi buildChatSystemPrompt lato backend). Qui quel testo viene spezzato nei
// suoi pezzi e ogni pezzo prende un colore: ambra il gancio, blu il corpo,
// verde la CTA — gli stessi colori della scheda contenuto del videomaker, così
// chi legge riconosce le parti al volo senza rileggere le etichette.
//
// Le didascalie non si mostrano: non servono al reel e allungano la risposta.
// Le etichette vecchie (HOOK / SCRIPT / DIDASCALIA) restano riconosciute,
// altrimenti le chat già salvate si vedrebbero come un muro di testo.
// ============================================================================

import React from 'react';

type Chiave =
  | 'gancio' | 'corpo' | 'cta'
  | 'pov' | 'scena' | 'audio'
  | 'dialogo' | 'carosello'
  | 'hashtag' | 'testo';

interface Sezione {
  chiave: Chiave;
  testo: string;
}

interface Proposta {
  titolo?: string;
  sezioni: Sezione[];
}

/** Etichette riconosciute, comprese quelle vecchie e le varianti in inglese. */
const ETICHETTE: { chiave: Chiave | 'titolo' | 'scarta'; nomi: string[] }[] = [
  { chiave: 'gancio', nomi: ['gancio', 'hook'] },
  // Formati visivi (POV, skit): la battuta sta nella scritta, non nel parlato.
  { chiave: 'pov', nomi: ['pov', 'scritta', 'testo a schermo', 'testo in grafica', 'testo grafica'] },
  { chiave: 'scena', nomi: ['scena', 'scene', 'clip', 'inquadratura', 'immagine', 'grafica'] },
  { chiave: 'audio', nomi: ['audio', 'suono'] },
  // Format che non sono né reel parlati né POV: interviste e caroselli.
  { chiave: 'dialogo', nomi: ['dialogo', 'intervista', 'battute'] },
  { chiave: 'carosello', nomi: ['carosello', 'slide', 'slides', 'schede'] },
  { chiave: 'corpo', nomi: ['corpo', 'script', 'body', 'contenuto', 'testo'] },
  { chiave: 'cta', nomi: ['cta', 'call to action'] },
  { chiave: 'hashtag', nomi: ['hashtag', 'hashtags'] },
  { chiave: 'titolo', nomi: ['titolo', 'title'] },
  // La didascalia viene letta solo per non finire dentro il corpo: non si mostra.
  { chiave: 'scarta', nomi: ['didascalia', 'caption', 'copy', 'copy post', 'post'] },
];

const RIGA_ETICHETTA = /^\s*[#>*\-•\d.)\s]*\*{0,2}\s*([a-zA-ZÀ-ÿ ]{2,20}?)\s*\*{0,2}\s*:\s*(.*)$/;
const RIGA_SEPARATORE = /^\s*(?:[-*_]\s*){3,}$/;
const RIGA_PROPOSTA = /^\s*[#*\s]*(?:proposta|variante|opzione|idea|reel)\s*[nN°.]*\s*(\d+)\s*[:.)\-–]?\s*\*{0,2}\s*$/i;

const etichettaDi = (parola: string): (Chiave | 'titolo' | 'scarta') | null => {
  const p = parola.trim().toLowerCase();
  return ETICHETTE.find((e) => e.nomi.includes(p))?.chiave ?? null;
};

/** Via gli asterischi del markdown: l'assistente risponde in testo semplice. */
const pulisci = (t: string): string => t.replace(/\*\*/g, '').trim();

/**
 * Il testo della risposta letto come elenco di proposte.
 * Ogni "---" apre una proposta nuova; dentro, le etichette aprono una sezione.
 */
export const leggiProposte = (testo: string): Proposta[] => {
  const proposte: Proposta[] = [];
  let corrente: Proposta = { sezioni: [] };
  let chiave: Chiave | 'scarta' = 'testo';
  let buffer: string[] = [];

  const chiudiSezione = () => {
    const contenuto = pulisci(buffer.join('\n'));
    if (contenuto && chiave !== 'scarta') corrente.sezioni.push({ chiave, testo: contenuto });
    buffer = [];
  };

  const chiudiProposta = () => {
    chiudiSezione();
    if (corrente.sezioni.length || corrente.titolo) proposte.push(corrente);
    corrente = { sezioni: [] };
    chiave = 'testo';
  };

  for (const riga of testo.replace(/\r\n/g, '\n').split('\n')) {
    if (RIGA_SEPARATORE.test(riga)) {
      chiudiProposta();
      continue;
    }

    const intestazione = riga.match(RIGA_PROPOSTA);
    if (intestazione) {
      chiudiProposta();
      corrente.titolo = `Proposta ${intestazione[1]}`;
      continue;
    }

    const etichetta = riga.match(RIGA_ETICHETTA);
    const chiaveRiga = etichetta ? etichettaDi(etichetta[1]) : null;
    if (etichetta && chiaveRiga) {
      // Un secondo gancio (o un secondo POV) significa che è cominciata un'altra
      // proposta anche senza separatore: l'assistente non sempre lo mette.
      if (
        (chiaveRiga === 'gancio' || chiaveRiga === 'pov') &&
        corrente.sezioni.some((s) => s.chiave === chiaveRiga)
      ) {
        chiudiProposta();
      } else {
        chiudiSezione();
      }
      if (chiaveRiga === 'titolo') {
        corrente.titolo = pulisci(etichetta[2]) || corrente.titolo;
        chiave = 'testo';
      } else {
        chiave = chiaveRiga;
        buffer.push(etichetta[2]);
      }
      continue;
    }

    buffer.push(riga);
  }
  chiudiProposta();

  return proposte;
};

const STILI: Record<Exclude<Chiave, 'testo' | 'hashtag'>, { label: string; barra: string; sfondo: string; testo: string }> = {
  gancio: {
    label: 'text-amber-700 dark:text-amber-400',
    barra: 'bg-amber-400 dark:bg-amber-500',
    sfondo: 'bg-amber-50/70 dark:bg-amber-900/15',
    testo: 'text-amber-950 dark:text-amber-50',
  },
  corpo: {
    label: 'text-blue-700 dark:text-blue-400',
    barra: 'bg-blue-400 dark:bg-blue-500',
    sfondo: 'bg-blue-50/70 dark:bg-blue-900/15',
    testo: 'text-blue-950 dark:text-blue-50',
  },
  cta: {
    label: 'text-emerald-700 dark:text-emerald-400',
    barra: 'bg-emerald-400 dark:bg-emerald-500',
    sfondo: 'bg-emerald-50/70 dark:bg-emerald-900/15',
    testo: 'text-emerald-950 dark:text-emerald-50',
  },
  // Il POV è la battuta: sta in evidenza come il gancio, ma di un altro colore
  // perché è un formato diverso e non va confuso con uno script parlato.
  pov: {
    label: 'text-violet-700 dark:text-violet-400',
    barra: 'bg-violet-400 dark:bg-violet-500',
    sfondo: 'bg-violet-50/70 dark:bg-violet-900/15',
    testo: 'text-violet-950 dark:text-violet-50',
  },
  scena: {
    label: 'text-sky-700 dark:text-sky-400',
    barra: 'bg-sky-400 dark:bg-sky-500',
    sfondo: 'bg-sky-50/70 dark:bg-sky-900/15',
    testo: 'text-sky-950 dark:text-sky-50',
  },
  audio: {
    label: 'text-slate-600 dark:text-slate-400',
    barra: 'bg-slate-300 dark:bg-slate-600',
    sfondo: 'bg-slate-50/70 dark:bg-gray-800/40',
    testo: 'text-slate-800 dark:text-slate-100',
  },
  dialogo: {
    label: 'text-teal-700 dark:text-teal-400',
    barra: 'bg-teal-400 dark:bg-teal-500',
    sfondo: 'bg-teal-50/70 dark:bg-teal-900/15',
    testo: 'text-teal-950 dark:text-teal-50',
  },
  carosello: {
    label: 'text-orange-700 dark:text-orange-400',
    barra: 'bg-orange-400 dark:bg-orange-500',
    sfondo: 'bg-orange-50/70 dark:bg-orange-900/15',
    testo: 'text-orange-950 dark:text-orange-50',
  },
};

const NOMI: Record<Exclude<Chiave, 'testo' | 'hashtag'>, string> = {
  gancio: 'Gancio',
  corpo: 'Corpo',
  cta: 'CTA',
  pov: 'Scritta nel video',
  scena: 'Cosa si gira',
  audio: 'Audio',
  dialogo: 'Dialogo',
  carosello: 'Schede del carosello',
};

const Blocco: React.FC<{ chiave: Exclude<Chiave, 'testo' | 'hashtag'>; testo: string }> = ({ chiave, testo }) => {
  const s = STILI[chiave];
  return (
    <div className={`flex gap-2.5 rounded-lg ${s.sfondo} pl-0 pr-3 py-2.5 overflow-hidden`}>
      <span className={`w-1 flex-shrink-0 rounded-full ${s.barra}`} aria-hidden />
      <div className="min-w-0">
        <p className={`text-[10px] font-bold uppercase tracking-wider mb-1 ${s.label}`}>{NOMI[chiave]}</p>
        <p className={`text-sm leading-relaxed whitespace-pre-wrap break-words ${s.testo}`}>{testo}</p>
      </div>
    </div>
  );
};

const Hashtag: React.FC<{ testo: string }> = ({ testo }) => {
  const tag = testo
    .split(/[\s,]+/)
    .map((t) => t.replace(/^#/, '').trim())
    .filter(Boolean);
  if (!tag.length) return null;
  return (
    <div className="flex flex-wrap gap-1 pl-3.5">
      {tag.map((t, i) => (
        <span
          key={`${t}-${i}`}
          className="px-1.5 py-0.5 rounded text-[10px] bg-slate-100 dark:bg-gray-700 text-slate-500 dark:text-slate-400"
        >
          #{t}
        </span>
      ))}
    </div>
  );
};

/**
 * La risposta dell'assistente. Se contiene proposte strutturate le mostra a
 * blocchi colorati, altrimenti resta il testo normale (domande, spiegazioni,
 * ragionamenti: lì i colori non servirebbero a niente).
 */
export const RispostaCopywriter: React.FC<{ testo: string }> = ({ testo }) => {
  const proposte = React.useMemo(() => leggiProposte(testo), [testo]);
  const strutturate = proposte.filter((p) => p.sezioni.some((s) => s.chiave !== 'testo'));

  if (!strutturate.length) {
    return (
      <p className="text-sm text-slate-800 dark:text-slate-100 whitespace-pre-wrap leading-relaxed">
        {pulisci(testo)}
      </p>
    );
  }

  const numerate = strutturate.length > 1;

  return (
    <div className="space-y-3">
      {proposte.map((p, i) => {
        const haBlocchi = p.sezioni.some((s) => s.chiave !== 'testo');
        // Testo libero fuori dalle proposte (intro, ragionamento): resta com'è.
        if (!haBlocchi) {
          return p.sezioni.map((s, j) => (
            <p
              key={`${i}-${j}`}
              className="text-sm text-slate-800 dark:text-slate-100 whitespace-pre-wrap leading-relaxed"
            >
              {s.testo}
            </p>
          ));
        }

        const indice = strutturate.indexOf(p) + 1;
        return (
          <div
            key={i}
            className="rounded-xl border border-slate-200 dark:border-gray-700 bg-slate-50/50 dark:bg-gray-900/30 p-2.5 space-y-2"
          >
            {(p.titolo || numerate) && (
              <div className="flex items-center gap-2 px-1 pt-0.5">
                {numerate && (
                  <span className="inline-flex items-center justify-center w-5 h-5 rounded-md text-[11px] font-bold bg-[#fdf751] text-slate-900">
                    {indice}
                  </span>
                )}
                {p.titolo && (
                  <p className="text-xs font-semibold text-slate-600 dark:text-slate-300 truncate">{p.titolo}</p>
                )}
              </div>
            )}

            {p.sezioni.map((s, j) => {
              if (s.chiave === 'hashtag') return <Hashtag key={j} testo={s.testo} />;
              if (s.chiave === 'testo') {
                return (
                  <p
                    key={j}
                    className="px-1 text-sm text-slate-800 dark:text-slate-100 whitespace-pre-wrap leading-relaxed"
                  >
                    {s.testo}
                  </p>
                );
              }
              return <Blocco key={j} chiave={s.chiave} testo={s.testo} />;
            })}
          </div>
        );
      })}
    </div>
  );
};

export default RispostaCopywriter;
